// Mapbox token will be imported dynamically where needed
// Note: Mapbox imports are avoided on web to prevent CSS bundling issues

import { Platform } from 'react-native';
import { getCachedGeocode, cacheGeocode } from '@/utils/geocodeCache';
import { retryWithBackoff, getUserFriendlyErrorMessage } from '@/utils/retry';
import {
  extractExplicitCityHintFromAddress,
  featureMatchesCityHintInContext,
  haversineDistance,
  validateGeocodeResult as validateResult,
} from '@/utils/geocodeHelpers';
import {
  recordMapboxCacheHit,
  recordMapboxCostError,
  tryConsumeMapboxBudget,
} from '@/config/mapboxCostPolicy';

let hasLoggedMissingToken = false;

/**
 * React Native fetch can occasionally hang indefinitely on Android emulators (DNS/SSL/network stalls).
 * To avoid blocking map screens (geocoding + place search), always use a hard timeout.
 */
const fetchWithTimeout = async (
  url: string,
  options?: RequestInit,
  timeoutMs: number = 12_000,
  externalSignal?: AbortSignal,
): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  const onExternalAbort = () => controller.abort();
  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort();
    } else {
      externalSignal.addEventListener('abort', onExternalAbort, { once: true });
    }
  }

  try {
    return await fetch(url, { ...(options ?? {}), signal: controller.signal });
  } catch (err: any) {
    // Normalize abort errors into stable codes that our retry layer can detect.
    if (err?.name === 'AbortError') {
      const abortedByCaller = !!externalSignal?.aborted;
      const e: any = new Error(
        abortedByCaller ? 'Request aborted' : `Request timed out after ${timeoutMs}ms`,
      );
      e.code = abortedByCaller ? 'EABORT' : 'ETIMEDOUT';
      throw e;
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
    if (externalSignal) {
      try {
        externalSignal.removeEventListener('abort', onExternalAbort);
      } catch {
        // ignore
      }
    }
  }
};

export type LocationCoordinates = {
  latitude: number;
  longitude: number;
};

export type GeocodeResult = {
  coordinates: [number, number]; // [longitude, latitude]
  placeName: string;
  /** Mapbox primary feature label (often the POI name); prefer over parsing `placeName`. */
  featureText?: string;
  /** Mapbox `properties.category` when present (POI coarse type). */
  poiCategory?: string;
  address?: string;
  context?: Array<{ id: string; text: string }>;
  placeTypes?: string[]; // Mapbox `place_type` (e.g. ["poi"], ["address"])
  relevance?: number;
};

export type ReverseGeocodeResult = {
  placeName: string;
  address?: string;
  coordinates: [number, number];
};

/** Error message when Cloud Function returns 429 (rate limit). Callers can show "showing cached pins" message. */
export const GEOCODE_RATE_LIMITED = 'GEOCODE_RATE_LIMITED';

/**
 * Geocode via Firebase Cloud Function (OpenWeatherMap). Used when Mapbox token
 * is missing or returns 403, so map pins still load.
 */
async function geocodeAddressViaCloudFunction(
  address: string,
  countryCode?: string
): Promise<GeocodeResult | null> {
  try {
    const { httpsCallable, functions } = await import('@/lib/functions');
    const geocodeLocation = httpsCallable<{ query: string; country?: string }, { lat: number; lon: number; name: string; country: string; state?: string }>(functions, 'geocodeLocation');
    const payload: { query: string; country?: string } = { query: address.trim() };
    if (countryCode && countryCode.length === 2) payload.country = countryCode;
    const result = await geocodeLocation(payload);
    const data = result?.data;
    if (!data || typeof data.lat !== 'number' || typeof data.lon !== 'number') return null;
    const resultOut: GeocodeResult = {
      coordinates: [data.lon, data.lat],
      placeName: data.name || address,
      address: data.name || address,
      context: data.country ? [{ id: 'country', text: data.country }] : undefined,
    };
    return resultOut;
  } catch (err: unknown) {
    const code = (err as { code?: string })?.code;
    if (code === 'resource-exhausted') {
      throw new Error(GEOCODE_RATE_LIMITED);
    }
    if (__DEV__) console.warn('[Maps] geocodeAddressViaCloudFunction failed:', err);
    return null;
  }
}

/**
 * Geocode an address to coordinates
 * Uses caching to improve performance and reduce API calls
 */
export const geocodeAddress = async (
  address: string,
  options?: {
    proximity?: LocationCoordinates;
    country?: string;
    /**
     * When set with `proximity`, only accept results within this distance (km) of the anchor.
     * Prevents same-state mismatches (e.g. NYC trip → Rochester) that still satisfy a loose US country filter.
     */
    maxDistanceFromProximityKm?: number;
    request?: {
      /** Hard timeout for a single HTTP request (ms). Default: 12s */
      timeoutMs?: number;
      /** Max retries for the geocode request. Default: 3 */
      maxRetries?: number;
      /** Optional abort signal to cancel in-flight requests */
      signal?: AbortSignal;
    };
  }
): Promise<GeocodeResult | null> => {
  const cacheOptions = options
    ? {
        country: options.country,
        proximity: options.proximity,
        maxDistanceKm: options.maxDistanceFromProximityKm,
      }
    : undefined;
  const cached = await getCachedGeocode(address, cacheOptions);
  if (cached) {
    recordMapboxCacheHit('geocode');
    // Validate cached result against options
    let isValid = true;
    const [lng, lat] = cached.coordinates;
    
    // Validate proximity if provided
    if (options?.proximity && isValid) {
      const distance = haversineDistance(
        { lat, lng },
        { lat: options.proximity.latitude, lng: options.proximity.longitude }
      );
      const maxAllowedKm = options.maxDistanceFromProximityKm ?? 1000;
      if (distance > maxAllowedKm) {
        if (__DEV__) {
          console.warn(
            `[Maps] Cached geocode result for "${address}" is ${Math.round(distance)}km from expected location (max ${maxAllowedKm}km). ` +
            `Invalidating cache and re-geocoding.`
          );
        }
        isValid = false;
      }
    }
    
    // Validate country if provided
    if (options?.country && isValid) {
      const resultCountry = cached.context?.find(ctx => ctx.id?.startsWith('country'))?.text?.toLowerCase();
      const expectedCountryCode = options.country.toLowerCase();
      
      const countryCodeMap: Record<string, string[]> = {
        'jp': ['japan', 'japanese'],
        'us': ['united states', 'usa', 'america'],
        'uk': ['united kingdom', 'britain', 'england'],
        'fr': ['france', 'french'],
        'de': ['germany', 'german'],
        'it': ['italy', 'italian'],
        'es': ['spain', 'spanish'],
        'ca': ['canada', 'canadian'],
        'au': ['australia', 'australian'],
        'dk': ['denmark', 'danish'],
        'se': ['sweden', 'swedish'],
        'no': ['norway', 'norwegian'],
        'vn': ['vietnam', 'vietnamese'],
        'ng': ['nigeria', 'nigerian'],
        'ma': ['morocco', 'moroccan'],
      };
      
      const expectedCountries = countryCodeMap[expectedCountryCode] || [];
      if (resultCountry && expectedCountries.length > 0) {
        const isCorrectCountry = expectedCountries.some(country =>
          resultCountry.includes(country)
        );
        
        if (!isCorrectCountry) {
          if (__DEV__) {
            console.warn(
              `[Maps] Cached geocode result for "${address}" is in wrong country (${resultCountry}, expected ${options.country}). ` +
              `Invalidating cache and re-geocoding.`
            );
          }
          isValid = false;
        }
      }
    }
    
    // If cached result is valid, return it
    if (isValid) {
    return cached;
    }
    
    // If cached result failed validation, remove it from cache and continue to re-geocode
    const { removeCachedGeocode } = await import('@/utils/geocodeCache');
    await removeCachedGeocode(address);
    if (__DEV__) {
      console.log(`[Maps] Removed invalid cached geocode for "${address}", re-geocoding...`);
    }
  }

  try {
    const { getMapboxAccessTokenSync, isMapboxTokenConfigured } = await import('@/config/mapbox');
    if (!isMapboxTokenConfigured()) {
      if (!hasLoggedMissingToken) {
        console.warn('[Maps] Mapbox access token missing; using Cloud Function fallback for geocode.');
        hasLoggedMissingToken = true;
      }
      try {
        const fallback = await geocodeAddressViaCloudFunction(address, options?.country);
        if (fallback) await cacheGeocode(address, fallback, cacheOptions);
        return fallback;
      } catch (e) {
        if (e instanceof Error && e.message === GEOCODE_RATE_LIMITED) throw e;
        return null;
      }
    }
    const accessToken = getMapboxAccessTokenSync();
    const geocodeBudget = tryConsumeMapboxBudget('geocode');
    if (!geocodeBudget.allowed) {
      if (__DEV__) {
        console.warn('[Maps] Geocoding blocked by cost policy:', geocodeBudget.reason, address);
      }
      return null;
    }
    const encodedAddress = encodeURIComponent(address);
    // Prioritize POI and address results over generic place names for venue-specific accuracy
    let url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedAddress}.json?access_token=${accessToken}&limit=5&types=poi,address,place`;
    
    // Add proximity if provided (helps with accuracy)
    if (options?.proximity) {
      url += `&proximity=${options.proximity.longitude},${options.proximity.latitude}`;
    }
    
    // Add country code if provided (helps disambiguate city names)
    if (options?.country) {
      url += `&country=${options.country}`;
    }

    const timeoutMs = options?.request?.timeoutMs ?? 12_000;
    const maxRetries = options?.request?.maxRetries ?? 3;
    const signal = options?.request?.signal;

    // Use retry logic for network requests
    const response = await retryWithBackoff(
      async () => {
        const res = await fetchWithTimeout(url, undefined, timeoutMs, signal);
        if (!res.ok) {
          const error: any = new Error(`Geocoding failed: ${res.statusText}`);
          error.status = res.status;
          throw error;
        }
        return res;
      },
      {
        maxRetries,
        initialDelay: 1000,
        retryable: (error: any) => {
          // Retry on network errors and 5xx errors, but not on 4xx (client errors)
          if (error?.code === 'EABORT') return false;
          return error?.status >= 500 || 
                 error?.message?.includes('NetworkError') || 
                 error?.message?.includes('Failed to fetch') ||
                 error?.code === 'ETIMEDOUT' ||
                 `${error?.message || ''}`.toLowerCase().includes('timed out');
        },
      }
    );

    const data = await response.json();
    if (data.features && data.features.length > 0) {
      const strictMax = options?.maxDistanceFromProximityKm;
      const prox = options?.proximity;
      const explicitCityHint = extractExplicitCityHintFromAddress(address);

      const placeTypeTier = (f: any): number => {
        const pt: unknown = f?.place_type;
        const arr = Array.isArray(pt) ? pt : typeof pt === 'string' ? [pt] : [];
        const s = arr.filter((t): t is string => typeof t === 'string');
        if (s.some((t) => t === 'poi' || t.startsWith('poi'))) return 4;
        if (s.includes('address')) return 3;
        if (s.some((t) => t === 'locality' || t === 'neighborhood')) return 2;
        if (s.includes('place')) return 1;
        return 0;
      };

      let bestFeature: any = null;

      if (strictMax != null && prox) {
        const scored = data.features
          .map((f: any) => {
            const [flng, flat] = f.geometry.coordinates as [number, number];
            const d = haversineDistance(
              { lat: flat, lng: flng },
              { lat: prox.latitude, lng: prox.longitude },
            );
            return {
              f,
              d,
              tier: placeTypeTier(f),
              rel: typeof f.relevance === 'number' ? f.relevance : 0,
            };
          })
          .filter((x: { d: number }) => x.d <= strictMax)
          .sort((a: { tier: number; rel: number; d: number }, b: { tier: number; rel: number; d: number }) => {
            if (b.tier !== a.tier) return b.tier - a.tier;
            if (Math.abs(b.rel - a.rel) > 0.02) return b.rel - a.rel;
            return a.d - b.d;
          });
        bestFeature = scored[0]?.f ?? null;
        if (!bestFeature) {
          if (__DEV__) {
            console.warn(
              `[Maps] Geocoding: no features within ${strictMax}km of proximity for "${address}" (${data.features.length} results)`,
            );
          }
          return null;
        }
      } else {
        bestFeature = data.features[0];
        if (data.features.length > 1) {
          const poiOrAddressFeatures = data.features.filter(
            (f: any) =>
              f.place_type?.includes('poi') ||
              f.place_type?.includes('address') ||
              f.properties?.category,
          );
          if (poiOrAddressFeatures.length > 0) {
            bestFeature = poiOrAddressFeatures.reduce((best: any, current: any) =>
              (current.relevance || 0) > (best.relevance || 0) ? current : best,
            );
          } else {
            bestFeature = data.features.reduce((best: any, current: any) =>
              (current.relevance || 0) > (best.relevance || 0) ? current : best,
            );
          }
        }
      }

      if (explicitCityHint) {
        if (!featureMatchesCityHintInContext(bestFeature, explicitCityHint)) {
          const cityMatchedAlt = data.features.find((f: any) =>
            featureMatchesCityHintInContext(f, explicitCityHint),
          );
          if (cityMatchedAlt) {
            if (__DEV__) {
              console.warn(
                `[Maps] Replacing geocode result with city-matched alternative for "${address}"`,
                { selected: bestFeature?.place_name, replacement: cityMatchedAlt?.place_name, cityHint: explicitCityHint },
              );
            }
            bestFeature = cityMatchedAlt;
          } else if (__DEV__) {
            console.warn(
              `[Maps] No geocode feature context matched city hint "${explicitCityHint}" for "${address}"`,
            );
          }
        }
      }

      const result: GeocodeResult = {
        coordinates: bestFeature.geometry.coordinates as [number, number],
        placeName: bestFeature.place_name,
        featureText: typeof bestFeature.text === 'string' ? bestFeature.text : undefined,
        poiCategory:
          typeof bestFeature.properties?.category === 'string' ? bestFeature.properties.category : undefined,
        address: bestFeature.properties?.address || bestFeature.place_name,
        context: bestFeature.context?.map((ctx: any) => ({
          id: ctx.id,
          text: ctx.text,
        })),
        placeTypes: Array.isArray(bestFeature.place_type)
          ? bestFeature.place_type.filter((t: unknown): t is string => typeof t === 'string')
          : typeof bestFeature.place_type === 'string'
            ? [bestFeature.place_type]
            : undefined,
        relevance: typeof bestFeature.relevance === 'number' ? bestFeature.relevance : undefined,
      };
      
      // Enhanced logging for debugging
      if (__DEV__) {
        const resultCountry = result.context?.find(ctx => ctx.id?.startsWith('country'))?.text;
        console.log(`[Maps] 🗺️ Geocoding result for "${address}":`, {
          placeName: result.placeName,
          coordinates: result.coordinates,
          country: resultCountry,
          countryCode: options?.country,
          proximity: options?.proximity,
          featureType: bestFeature.place_type,
          relevance: bestFeature.relevance,
          allFeatures: data.features.length,
        });
      }
      
      // Validate coordinates are reasonable before caching
      const [lng, lat] = result.coordinates;
      const usedStrictProximity =
        options?.maxDistanceFromProximityKm != null && options.proximity != null;

      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        // If proximity was provided, validate that the result is reasonably close
        if (options?.proximity && !usedStrictProximity) {
          const distance = haversineDistance(
            { lat, lng },
            { lat: options.proximity.latitude, lng: options.proximity.longitude },
          );

          // If result is more than 1000km from the expected location, it's likely wrong
          if (distance > 1000) {
            console.warn(
              `[Maps] Geocoding result "${result.placeName}" is ${Math.round(distance)}km from expected location. ` +
                `Address: "${address}". Proximity: ${options.proximity.latitude}, ${options.proximity.longitude}. ` +
                `Result: ${lat}, ${lng}. Skipping this result.`,
            );
            // Try to find a closer result from the features array
            if (data.features.length > 1) {
              for (const feature of data.features.slice(1)) {
                const [altLng, altLat] = feature.geometry.coordinates;
                const altDistance = haversineDistance(
                  { lat: altLat, lng: altLng },
                  { lat: options.proximity.latitude, lng: options.proximity.longitude },
                );
                if (altDistance < 500) {
                  console.log(
                    `[Maps] Found better alternative result: ${feature.place_name} (${Math.round(altDistance)}km away)`,
                  );
                  const altResult: GeocodeResult = {
                    coordinates: [altLng, altLat] as [number, number],
                    placeName: feature.place_name,
                    featureText: typeof feature.text === 'string' ? feature.text : undefined,
                    poiCategory:
                      typeof feature.properties?.category === 'string'
                        ? feature.properties.category
                        : undefined,
                    address: feature.properties?.address || feature.place_name,
                    context: feature.context?.map((ctx: any) => ({
                      id: ctx.id,
                      text: ctx.text,
                    })),
                    placeTypes: Array.isArray(feature.place_type)
                      ? feature.place_type.filter((t: unknown): t is string => typeof t === 'string')
                      : typeof feature.place_type === 'string'
                        ? [feature.place_type]
                        : undefined,
                    relevance: typeof feature.relevance === 'number' ? feature.relevance : undefined,
                  };
                  await cacheGeocode(address, altResult, cacheOptions);
                  return altResult;
                }
              }
            }
            // If no good alternative found and we have country code, reject the result
            // This prevents caching obviously wrong geocodes
            if (options?.country) {
              console.error(
                `[Maps] Geocoding failed: Result is ${Math.round(distance)}km from expected location ` +
                  `and country code "${options.country}" was specified. Rejecting result to prevent caching wrong data.`,
              );
              return null; // Don't cache or return wrong results
            }
            // If no good alternative found, still return the result but log warning
            console.warn('[Maps] No closer alternative found, using original result (may be incorrect)');
          }
        }
        
        // Additional validation: If country code was provided, check that result is in that country
        if (options?.country) {
          const resultCountry = result.context?.find(ctx => ctx.id?.startsWith('country'))?.text?.toLowerCase();
          const expectedCountryCode = options.country.toLowerCase();
          
          // Map country codes to country names for validation
          const countryCodeMap: Record<string, string[]> = {
            'jp': ['japan', 'japanese'],
            'us': ['united states', 'usa', 'america'],
            'uk': ['united kingdom', 'britain', 'england'],
            'fr': ['france', 'french'],
            'de': ['germany', 'german'],
            'it': ['italy', 'italian'],
            'es': ['spain', 'spanish'],
            'ca': ['canada', 'canadian'],
            'au': ['australia', 'australian'],
            'dk': ['denmark', 'danish'],
            'se': ['sweden', 'swedish'],
            'no': ['norway', 'norwegian'],
            'vn': ['vietnam', 'vietnamese'],
            'ng': ['nigeria', 'nigerian'],
            'ma': ['morocco', 'moroccan'],
          };
          
          const expectedCountries = countryCodeMap[expectedCountryCode] || [];
          if (resultCountry && expectedCountries.length > 0) {
            const isCorrectCountry = expectedCountries.some(country =>
              resultCountry.includes(country)
            );
            
            if (!isCorrectCountry) {
              console.error(
                `[Maps] ❌ Geocoding country mismatch: Expected country code "${options.country}" ` +
                `but result is in "${resultCountry}". Address: "${address}". ` +
                `Result: ${result.placeName} at ${lat}, ${lng}. Rejecting result.`
              );
              if (__DEV__) {
                console.error(`[Maps] Debug info:`, {
                  address,
                  expectedCountry: options.country,
                  expectedCountries,
                  resultCountry,
                  resultPlaceName: result.placeName,
                  resultCoords: [lng, lat],
                  allFeatures: data.features.map((f: any) => ({
                    place_name: f.place_name,
                    country: f.context?.find((ctx: any) => ctx.id?.startsWith('country'))?.text,
                    coords: f.geometry.coordinates,
                  })),
                });
              }
              
              // Try to find a result in the correct country
              if (data.features.length > 1) {
                for (const feature of data.features.slice(1)) {
                  const featureCountry = feature.context?.find((ctx: any) => ctx.id?.startsWith('country'))?.text?.toLowerCase();
                  if (featureCountry && expectedCountries.some(country => featureCountry.includes(country))) {
                    console.log(`[Maps] Found result in correct country: ${feature.place_name}`);
                    const [altLng, altLat] = feature.geometry.coordinates;
                    const altResult: GeocodeResult = {
                      coordinates: [altLng, altLat] as [number, number],
                      placeName: feature.place_name,
                      address: feature.properties?.address || feature.place_name,
                      context: feature.context?.map((ctx: any) => ({
                        id: ctx.id,
                        text: ctx.text,
                      })),
                    };
                    await cacheGeocode(address, altResult, cacheOptions);
                    return altResult;
                  }
                }
              }
              
              // Reject the result if it's in the wrong country
              return null;
            }
          }
        }
        
        // Validate result before caching - don't cache obviously wrong results
        // Check if result makes sense for the address (basic validation)
        // Note: lat and lng are already declared above, no need to redeclare
        if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 && !(lat === 0 && lng === 0)) {
          // Country validation was already done above, so if we reach here, result is valid
          // Cache the result only if it passed all validations
          await cacheGeocode(address, result, cacheOptions);
          if (__DEV__) {
            console.log(`[Maps] ✅ Cached geocode result for "${address}"`);
          }
          return result;
        } else {
          console.warn('[Maps] Not caching invalid geocode result:', result.coordinates);
          return null;
        }
      } else {
        console.warn('[Maps] Geocoding returned invalid coordinates:', result.coordinates);
        return null;
      }
    }

    return null;
  } catch (error) {
    recordMapboxCostError('geocode');
    // For 401/403 (invalid/expired token), mark token rejected so UI shows setup modal, then try Cloud Function fallback
    if (error && typeof error === 'object' && 'status' in error) {
      const status = (error as { status?: number }).status;
      if (status === 401 || status === 403) {
        const { markMapboxTokenRejected } = await import('@/config/mapbox');
        markMapboxTokenRejected();
        if (__DEV__) {
          console.warn('[Maps] Mapbox returned', status, '- using Cloud Function fallback for:', address);
        }
        try {
          const fallback = await geocodeAddressViaCloudFunction(address, options?.country);
          if (fallback) {
            await cacheGeocode(address, fallback, cacheOptions);
            return fallback;
          }
        } catch (fallbackErr) {
          if (fallbackErr instanceof Error && fallbackErr.message === GEOCODE_RATE_LIMITED) throw fallbackErr;
          console.warn('[Maps] Cloud Function fallback failed:', fallbackErr);
        }
        return null;
      }
    }
    // Log other errors
    const userMessage = getUserFriendlyErrorMessage(error);
    console.error('[Maps] Error geocoding address:', error);
    console.error('[Maps] User-friendly message:', userMessage);
    return null;
  }
};

/**
 * Reverse geocode coordinates to address
 */
export const reverseGeocode = async (
  longitude: number,
  latitude: number
): Promise<ReverseGeocodeResult | null> => {
  try {
    const reverseBudget = tryConsumeMapboxBudget('reverseGeocode');
    if (!reverseBudget.allowed) {
      if (__DEV__) {
        console.warn('[Maps] Reverse geocode blocked by cost policy:', reverseBudget.reason);
      }
      return null;
    }
    const { getMapboxAccessTokenSync, isMapboxTokenConfigured } = await import('@/config/mapbox');
    if (!isMapboxTokenConfigured()) {
      if (!hasLoggedMissingToken) {
        console.warn('[Maps] Mapbox access token missing; skipping reverse geocode. Add EXPO_PUBLIC_MAPBOX_ACCESS_TOKEN.');
        hasLoggedMissingToken = true;
      }
      return null;
    }
    const accessToken = getMapboxAccessTokenSync();
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${accessToken}&limit=1`;

    const response = await fetchWithTimeout(url, undefined, 10_000);
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        const { markMapboxTokenRejected } = await import('@/config/mapbox');
        markMapboxTokenRejected();
      }
      throw new Error(`Reverse geocoding failed: ${response.statusText}`);
    }

    const data = await response.json();
    if (data.features && data.features.length > 0) {
      const feature = data.features[0];
      return {
        placeName: feature.place_name,
        address: feature.properties?.address || feature.place_name,
        coordinates: feature.geometry.coordinates as [number, number],
      };
    }

    return null;
  } catch (error) {
    recordMapboxCostError('reverseGeocode');
    console.error('[Maps] Error reverse geocoding:', error);
    return null;
  }
};

/** Parse Search Box `FeatureCollection` JSON into `GeocodeResult` (POI features only). */
function searchBoxFeatureCollectionToGeocodeResults(data: unknown): GeocodeResult[] {
  const features: any[] = Array.isArray((data as any)?.features) ? (data as any).features : [];
  const n = features.length;
  const out: GeocodeResult[] = [];

  for (let index = 0; index < features.length; index++) {
    const feat = features[index];
    const props = feat?.properties;
    if (!props || props.feature_type !== 'poi') continue;
    const coords = feat?.geometry?.coordinates as [number, number] | undefined;
    if (!coords || coords.length < 2) continue;

    const name = typeof props.name === 'string' ? props.name : '';
    const full =
      typeof props.full_address === 'string'
        ? props.full_address
        : [name, typeof props.place_formatted === 'string' ? props.place_formatted : '']
            .filter(Boolean)
            .join(', ');

    const ids = Array.isArray(props.poi_category_ids) ? props.poi_category_ids : [];
    const poiCat = typeof ids[0] === 'string' ? ids[0] : undefined;

    out.push({
      coordinates: coords,
      placeName: full || name,
      featureText: typeof props.name_preferred === 'string' ? props.name_preferred : name,
      poiCategory: poiCat,
      address: typeof props.address === 'string' ? props.address : full || name,
      placeTypes: ['poi'],
      relevance: Math.max(0.45, 1 - index / Math.max(n, 1)),
    });
  }

  return out;
}

/**
 * Mapbox Search Box `/forward` — text POI search (max 10 results per request).
 * @see https://docs.mapbox.com/api/search/search-box/#get-search-results
 */
export const searchBoxForwardNearby = async (options: {
  query: string;
  proximity: LocationCoordinates;
  limit?: number;
  country?: string;
  language?: string;
  types?: string;
  poiCategory?: string;
  ignoreCostPolicy?: boolean;
}): Promise<GeocodeResult[]> => {
  try {
    if (!options.ignoreCostPolicy) {
      const budget = tryConsumeMapboxBudget('searchBox');
      if (!budget.allowed) {
        if (__DEV__) {
          console.warn('[Maps] Search Box forward blocked by cost policy:', budget.reason, options.query);
        }
        return [];
      }
    }
    const { getMapboxAccessTokenSync, isMapboxTokenConfigured } = await import('@/config/mapbox');
    if (!isMapboxTokenConfigured()) return [];

    const token = getMapboxAccessTokenSync();
    const { query, proximity, limit = 10, country, language = 'en', types = 'poi', poiCategory } = options;
    const params = new URLSearchParams({
      access_token: token,
      q: query,
      language,
      limit: String(Math.min(10, Math.max(1, limit))),
      proximity: `${proximity.longitude},${proximity.latitude}`,
      types,
    });
    if (country) params.set('country', country);
    if (poiCategory) params.set('poi_category', poiCategory);

    const url = `https://api.mapbox.com/search/searchbox/v1/forward?${params.toString()}`;
    const response = await fetchWithTimeout(url, undefined, 12_000);
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        const { markMapboxTokenRejected } = await import('@/config/mapbox');
        markMapboxTokenRejected();
      }
      if (__DEV__) {
        const errBody = await response.text().catch(() => '');
        console.warn('[Maps] Search Box forward HTTP', query, response.status, errBody.slice(0, 160));
      }
      return [];
    }

    const data = await response.json();
    return searchBoxFeatureCollectionToGeocodeResults(data);
  } catch (e) {
    recordMapboxCostError('searchBox');
    if (__DEV__) console.warn('[Maps] searchBoxForwardNearby error:', options.query, e);
    return [];
  }
};

/**
 * Mapbox Search Box API — category POI browse (real named venues, not admin polygons).
 * @see https://docs.mapbox.com/api/search/search-box/#category-search
 */
export const searchBoxCategoryNearby = async (
  categoryId: string,
  options: {
    proximity: LocationCoordinates;
    limit?: number;
    country?: string;
    language?: string;
    /** minLon,minLat,maxLon,maxLat — strongly recommended to avoid distant same-name hits */
    bbox?: string;
    ignoreCostPolicy?: boolean;
  },
): Promise<GeocodeResult[]> => {
  try {
    if (!options.ignoreCostPolicy) {
      const budget = tryConsumeMapboxBudget('searchBox');
      if (!budget.allowed) {
        if (__DEV__) {
          console.warn('[Maps] Search Box category blocked by cost policy:', budget.reason, categoryId);
        }
        return [];
      }
    }
    const { getMapboxAccessTokenSync, isMapboxTokenConfigured } = await import('@/config/mapbox');
    if (!isMapboxTokenConfigured()) return [];

    const token = getMapboxAccessTokenSync();
    const { proximity, limit = 12, country, language = 'en', bbox } = options;
    const params = new URLSearchParams({
      access_token: token,
      language,
      limit: String(Math.min(25, Math.max(1, limit))),
      proximity: `${proximity.longitude},${proximity.latitude}`,
      types: 'poi',
    });
    if (country) params.set('country', country);
    if (bbox) params.set('bbox', bbox);

    const url = `https://api.mapbox.com/search/searchbox/v1/category/${encodeURIComponent(categoryId)}?${params.toString()}`;
    const response = await fetchWithTimeout(url, undefined, 12_000);
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        const { markMapboxTokenRejected } = await import('@/config/mapbox');
        markMapboxTokenRejected();
      }
      if (__DEV__) {
        const errBody = await response.text().catch(() => '');
        console.warn('[Maps] Search Box category HTTP', categoryId, response.status, errBody.slice(0, 180));
      }
      return [];
    }

    const data = await response.json();
    return searchBoxFeatureCollectionToGeocodeResults(data);
  } catch (e) {
    recordMapboxCostError('searchBox');
    if (__DEV__) console.warn('[Maps] searchBoxCategoryNearby error:', categoryId, e);
    return [];
  }
};

/**
 * Mapbox Tilequery API — pulls raw POIs from the `mapbox-streets-v8` tileset near a point.
 *
 * Unlike the Search Box category / forward endpoints (which have US-centric coverage),
 * tilequery reads directly from the same vector tiles that render the map, so it returns
 * results anywhere in the world that has POIs on the basemap. Used as a final fallback
 * for locales where Search Box returns 0 (observed for Tokyo, Lima, etc.).
 *
 * @see https://docs.mapbox.com/api/maps/tilequery/
 */
export const tilequeryPoiNearby = async (options: {
  proximity: LocationCoordinates;
  /** Radius in meters. Mapbox allows up to 100,000 for `mapbox-streets-v8`. */
  radiusMeters?: number;
  limit?: number;
  /** Filter by poi_label.class (authoritative list from streets-v8 reference). */
  classes?: string[];
  /**
   * Optional filter on poi_label.type (e.g. 'Museum', 'Park'). Match is case-insensitive
   * substring. Use when you want to drill into a class (e.g. `arts_and_entertainment`
   * → only museums/galleries).
   */
  types?: string[];
  /** Max filterrank to accept. 0 = most prominent, 5 = least. Default 5 (keep everything). */
  maxFilterRank?: number;
  language?: string;
  ignoreCostPolicy?: boolean;
}): Promise<GeocodeResult[]> => {
  try {
    if (!options.ignoreCostPolicy) {
      const budget = tryConsumeMapboxBudget('tilequery');
      if (!budget.allowed) {
        if (__DEV__) {
          console.warn('[Maps] Tilequery blocked by cost policy:', budget.reason);
        }
        return [];
      }
    }
    const { getMapboxAccessTokenSync, isMapboxTokenConfigured } = await import('@/config/mapbox');
    if (!isMapboxTokenConfigured()) return [];

    const token = getMapboxAccessTokenSync();
    const {
      proximity,
      radiusMeters = 5000,
      limit = 50,
      classes,
      types,
      maxFilterRank = 5,
      language = 'en',
    } = options;

    const params = new URLSearchParams({
      access_token: token,
      radius: String(Math.min(100_000, Math.max(50, radiusMeters))),
      limit: String(Math.min(50, Math.max(1, limit))),
      layers: 'poi_label',
      geometry: 'point',
      dedupe: 'true',
    });

    const url =
      `https://api.mapbox.com/v4/mapbox.mapbox-streets-v8/tilequery/` +
      `${proximity.longitude},${proximity.latitude}.json?${params.toString()}`;
    const response = await fetchWithTimeout(url, undefined, 12_000);
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        const { markMapboxTokenRejected } = await import('@/config/mapbox');
        markMapboxTokenRejected();
      }
      if (__DEV__) {
        const errBody = await response.text().catch(() => '');
        console.warn('[Maps] tilequery HTTP', response.status, errBody.slice(0, 180));
      }
      return [];
    }

    const data = await response.json();
    const features: any[] = Array.isArray((data as any)?.features) ? (data as any).features : [];
    const allowedClasses = classes ? new Set(classes) : null;
    const typeMatchers = types
      ? types.map((t) => t.toLowerCase()).filter((t) => t.length > 0)
      : null;
    const out: GeocodeResult[] = [];
    const n = features.length;

    // Per-call diagnostics — helps pinpoint whether a low final count is from
    // the API itself (raw=0), the class filter, or filterrank. Essential for
    // debugging international-city landmark gaps (Tokyo, Lima, etc.).
    const classCounts = new Map<string, number>();
    let droppedNoName = 0;
    let droppedClass = 0;
    let droppedType = 0;
    let droppedRank = 0;

    for (let index = 0; index < features.length; index++) {
      const feat = features[index];
      const props = feat?.properties || {};
      const coords = feat?.geometry?.coordinates as [number, number] | undefined;
      if (!coords || coords.length < 2) continue;

      const klassRaw = typeof props.class === 'string' ? props.class : 'unknown';
      classCounts.set(klassRaw, (classCounts.get(klassRaw) || 0) + 1);

      // Name preference: `name_<lang>` → `name_en` → global `name` (which may be native script).
      // Mapbox v8 sets translated name fields to null when no translation exists, so falls through.
      const nameLang = typeof props[`name_${language}`] === 'string' ? props[`name_${language}`] : '';
      const nameEn = typeof props.name_en === 'string' ? props.name_en : '';
      const nameAny = typeof props.name === 'string' ? props.name : '';
      const name: string = nameLang || nameEn || nameAny || '';
      if (!name) {
        droppedNoName += 1;
        continue;
      }

      const klass = typeof props.class === 'string' ? props.class : undefined;
      if (allowedClasses && (!klass || !allowedClasses.has(klass))) {
        droppedClass += 1;
        continue;
      }

      if (typeMatchers) {
        const poiType = (typeof props.type === 'string' ? props.type : '').toLowerCase();
        if (!poiType || !typeMatchers.some((t) => poiType.includes(t))) {
          droppedType += 1;
          continue;
        }
      }

      // filterrank: 0 = most important, 5 = least. Zoom-relative.
      const rank = typeof props.filterrank === 'number' ? props.filterrank : 0;
      if (rank > maxFilterRank) {
        droppedRank += 1;
        continue;
      }

      out.push({
        coordinates: coords,
        placeName: name,
        featureText: name,
        poiCategory: klass,
        address: name,
        placeTypes: ['poi'],
        relevance: Math.max(0.3, 1 - index / Math.max(n, 1) - rank * 0.05),
      });
    }

    if (__DEV__) {
      const byClass = Array.from(classCounts.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 8);
      console.warn('[Maps] tilequery diag', {
        proximity: `${proximity.latitude.toFixed(3)},${proximity.longitude.toFixed(3)}`,
        radiusM: radiusMeters,
        raw: features.length,
        kept: out.length,
        droppedNoName,
        droppedClass,
        droppedType,
        droppedRank,
        topClasses: byClass,
        filteringBy: allowedClasses ? Array.from(allowedClasses) : 'none',
        maxFilterRank,
      });
    }

    return out;
  } catch (e) {
    recordMapboxCostError('tilequery');
    if (__DEV__) console.warn('[Maps] tilequeryPoiNearby error:', e);
    return [];
  }
};

/**
 * Search for places using Mapbox Geocoding API
 */
export const searchPlaces = async (
  query: string,
  options?: {
    proximity?: [number, number]; // [longitude, latitude]
    limit?: number;
    types?: string[];
    country?: string; // ISO-3166 alpha-2 country code (Mapbox `country=` filter)
    language?: string;
    autocomplete?: boolean;
    fuzzyMatch?: boolean;
    useCache?: boolean; // Whether to use cache (default: true)
    ignoreCostPolicy?: boolean;
    request?: {
      signal?: AbortSignal;
    };
  }
): Promise<GeocodeResult[]> => {
  try {
    // Check cache first
    if (options?.useCache !== false) {
      const { getCachedSearchResults, cacheSearchResults } = await import('@/utils/searchCache');
      const optionsKey = JSON.stringify({
        // Only include options that affect results ordering/contents
        proximity: options?.proximity ? [Number(options.proximity[0].toFixed(4)), Number(options.proximity[1].toFixed(4))] : null,
        types: options?.types ?? null,
        country: options?.country ?? null,
        language: options?.language ?? null,
        autocomplete: options?.autocomplete ?? true,
        fuzzyMatch: options?.fuzzyMatch ?? true,
        limit: options?.limit ?? null,
      });
      const cached = await getCachedSearchResults(query, options?.proximity, optionsKey);
      if (cached) {
        recordMapboxCacheHit('search');
        console.log('[Maps] Using cached search results for:', query);
        return cached;
      }
    }

    if (!options?.ignoreCostPolicy) {
      const searchBudget = tryConsumeMapboxBudget('search');
      if (!searchBudget.allowed) {
        if (__DEV__) {
          console.warn('[Maps] Place search blocked by cost policy:', searchBudget.reason, query);
        }
        return [];
      }
    }

    const { getMapboxAccessTokenSync, isMapboxTokenConfigured } = await import('@/config/mapbox');
    if (!isMapboxTokenConfigured()) {
      throw new Error('Mapbox access token is missing or temporarily blocked. Please check map setup.');
    }
    const accessToken = getMapboxAccessTokenSync();
    const encodedQuery = encodeURIComponent(query);
    let url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedQuery}.json?access_token=${accessToken}`;

    const autocomplete = options?.autocomplete ?? true;
    const fuzzyMatch = options?.fuzzyMatch ?? true;
    const language = options?.language ?? 'en';
    url += `&autocomplete=${autocomplete ? 'true' : 'false'}`;
    url += `&fuzzyMatch=${fuzzyMatch ? 'true' : 'false'}`;
    if (language) url += `&language=${encodeURIComponent(language)}`;

    if (options?.proximity) {
      url += `&proximity=${options.proximity[0]},${options.proximity[1]}`;
    }

    if (options?.limit) {
      url += `&limit=${options.limit}`;
    } else {
      url += '&limit=10';
    }

    if (options?.types && options.types.length > 0) {
      url += `&types=${options.types.join(',')}`;
    }

    if (options?.country) {
      url += `&country=${options.country}`;
    }

    const response = await fetchWithTimeout(url, undefined, 10_000, options?.request?.signal);
    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error('[Maps] Place search failed:', response.status, errorText);
      
      if (response.status === 401 || response.status === 403) {
        const { markMapboxTokenRejected } = await import('@/config/mapbox');
        markMapboxTokenRejected();
        throw new Error('Mapbox access token is invalid or expired. Please check your configuration.');
      }
      throw new Error(`Search failed: ${response.statusText}`);
    }

    const data = await response.json();
    
    // Check for API errors in response
    if (data.error) {
      console.error('[Maps] Mapbox API error:', data.error);
      throw new Error(data.error.message || 'Search failed');
    }
    
    let results: GeocodeResult[] = [];
    
    if (data.features && data.features.length > 0) {
      const normalizePlaceTypes = (placeType: unknown): string[] | undefined => {
        if (Array.isArray(placeType)) return placeType.filter((t) => typeof t === 'string');
        if (typeof placeType === 'string') return [placeType];
        return undefined;
      };

      results = data.features.map((feature: any) => ({
        coordinates: feature.geometry.coordinates as [number, number],
        placeName: feature.place_name,
        featureText: typeof feature.text === 'string' ? feature.text : undefined,
        poiCategory:
          typeof feature.properties?.category === 'string' ? feature.properties.category : undefined,
        address: feature.properties?.address || feature.place_name,
        context: feature.context?.map((ctx: any) => ({
          id: ctx.id,
          text: ctx.text,
        })),
        placeTypes: normalizePlaceTypes(feature.place_type),
        relevance: typeof feature.relevance === 'number' ? feature.relevance : undefined,
      }));

      // Cache results
      if (options?.useCache !== false && results.length > 0) {
        const { cacheSearchResults } = await import('@/utils/searchCache');
        const optionsKey = JSON.stringify({
          proximity: options?.proximity ? [Number(options.proximity[0].toFixed(4)), Number(options.proximity[1].toFixed(4))] : null,
          types: options?.types ?? null,
          country: options?.country ?? null,
          language: options?.language ?? null,
          autocomplete: options?.autocomplete ?? true,
          fuzzyMatch: options?.fuzzyMatch ?? true,
          limit: options?.limit ?? null,
        });
        await cacheSearchResults(query, results, options?.proximity, optionsKey);
      }
    }

    return results;
  } catch (error) {
    recordMapboxCostError('search');
    console.error('[Maps] Error searching places:', error);
    // Re-throw to allow caller to handle
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred while searching');
  }
};

/**
 * Download offline map tiles for a region
 * Note: @rnmapbox/maps has offline pack support, but this is a simplified version
 * For full offline support, you may need to use Mapbox's offline pack manager
 */
export type OfflineMapRegion = {
  name: string;
  bounds: {
    ne: [number, number]; // [longitude, latitude]
    sw: [number, number]; // [longitude, latitude]
  };
  minZoom: number;
  maxZoom: number;
};

/**
 * Get bounds for a city/region
 * This is a helper to create bounds for offline download
 */
export const getCityBounds = async (cityName: string): Promise<OfflineMapRegion['bounds'] | null> => {
  try {
    const geocodeResult = await geocodeAddress(cityName);
    if (!geocodeResult) {
      return null;
    }

    // For a city, we'll create a bounding box around the center
    // In a real implementation, you'd want to get actual city boundaries
    const [lon, lat] = geocodeResult.coordinates;
    const padding = 0.1; // ~11km padding

    return {
      ne: [lon + padding, lat + padding],
      sw: [lon - padding, lat - padding],
    };
  } catch (error) {
    console.error('[Maps] Error getting city bounds:', error);
    return null;
  }
};

// Mapbox export - only available on native platforms
// Web platform will get undefined, components should handle this
export const getMapboxSDK = () => {
  // Web platform - native Mapbox not supported
  if (Platform.OS === 'web') return null;
  try {
    // Dynamic require only on native platforms
    const RNMapbox = require('@rnmapbox/maps');
    return RNMapbox.default || RNMapbox;
  } catch (error) {
    console.warn('[Maps] Mapbox SDK not available:', error);
    return null;
  }
};

